import { useCallback } from 'react';

import { publishBoardSocketPackage } from '@walls/services/boardWebsockets';
import { wallsApi } from '@walls/services/wallsApi';
import {
  useBoardSelectionStore,
  useWallContentStore,
  useWallStore,
  useWorkspaceUiStore,
  type CardId,
  type ConnectionId,
  type WallBoard,
} from '@walls/store';
import { deleteConnectionsByCardId } from './connectionDeletion';
import { connectionsAreBetweenCards } from './connectionGeometry';

export function useConnectionCommands(board: WallBoard) {
  const addConnection = useWallContentStore((state) => state.addConnection);
  const connections = useWallContentStore((state) => state.connections);
  const removeConnection = useWallContentStore((state) => state.removeConnection);
  const removeConnectionsByParticipant = useWallContentStore(
    (state) => state.removeConnectionsByParticipant
  );
  const updateConnection = useWallContentStore((state) => state.updateConnection);
  const setCurrentElement = useBoardSelectionStore((state) => state.setCurrentElement);
  const setConnectingFromId = useBoardSelectionStore((state) => state.setConnectingFromId);
  const selectedPenColor = useWorkspaceUiStore((state) => state.selectedPenColor);
  const topicKey = useWallStore((state) => state.topicKey);

  const deleteConnection = useCallback(
    async (connectionId: ConnectionId) => {
      removeConnection(connectionId);
      setCurrentElement(null);

      await wallsApi.deleteConnection(board.id, connectionId, topicKey);
      publishBoardSocketPackage({
        action: 'connectionDeleted',
        topic: board.id,
        connection: { id: connectionId },
      });
    },
    [board.id, removeConnection, setCurrentElement, topicKey]
  );

  return {
    deleteConnection,

    deleteConnectionsByCardId: useCallback(
      async (cardId: CardId) => {
        await deleteConnectionsByCardId(board.id, cardId, topicKey);
      },
      [board.id, topicKey]
    ),

    insertConnection: useCallback(
      async (startCardId: CardId, endCardId: CardId) => {
        if (startCardId === endCardId) {
          setConnectingFromId(null);
          return null;
        }

        const duplicateConnections = connections.filter((connection) =>
          connectionsAreBetweenCards(connection, startCardId, endCardId)
        );

        duplicateConnections.forEach((connection) => removeConnection(connection.id));

        await Promise.all(
          duplicateConnections.map((connection) =>
            wallsApi.deleteConnection(board.id, connection.id, topicKey)
          )
        );

        const savedConnection = await wallsApi.insertConnection(
          board.id,
          '',
          selectedPenColor,
          startCardId,
          endCardId,
          topicKey
        );

        addConnection(savedConnection);
        setConnectingFromId(null);
        publishBoardSocketPackage({
          action: 'connectionInserted',
          topic: board.id,
          connection: savedConnection,
        });

        return savedConnection;
      },
      [
        addConnection,
        board.id,
        connections,
        removeConnection,
        selectedPenColor,
        setConnectingFromId,
        topicKey,
      ]
    ),

    removeConnectionsByParticipant,

    updateConnectionColor: useCallback(
      async (connectionId: ConnectionId, color: string) => {
        updateConnection(connectionId, { color });
        await wallsApi.updateConnectionColor(board.id, connectionId, color, topicKey);
        publishBoardSocketPackage({
          action: 'connectionColorUpdated',
          topic: board.id,
          connectionId,
          color,
        });
      },
      [board.id, topicKey, updateConnection]
    ),
  };
}
